import { Tooltip, Modal as AntModal } from 'antd'
import { TemplateNameIndicator } from './TemplateNameIndicator'
import { useEffect, useMemo, useState } from 'react'
import { Queries } from 'admin/lib/Api'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

// It seems that MatchedRecord can be null or exist with null values.
type MatchedRecordOnRecord = {
  projectId: string | null
  recordId: string | null
} | null

type MatchedRecord = {
  projectId: string
  recordId: string
}

export function TemplateNameLabel({
  templateName,
  estimatedAccuracy,
  matchedRecord,
  moduleName
}: {
  templateName: string | null
  estimatedAccuracy: number | null
  matchedRecord: MatchedRecordOnRecord
  moduleName: string
}) {
  const [linkOpening, setLinkOpening] = useState(false)

  if (!templateName) {
    return null
  }
  return (
    <Tooltip
      title={
        estimatedAccuracy ? `Estimated Accuracy: ${estimatedAccuracy}%` : ''
      }
      placement="topLeft"
    >
      <div style={{ display: 'flex', alignItems: 'center', margin: '3px 0' }}>
        <TemplateNameIndicator estimatedAccuracy={estimatedAccuracy} />

        {matchedRecord == null ||
        matchedRecord.projectId == null ||
        matchedRecord.recordId == null ? (
          <div>{templateName}</div>
        ) : (
          <FakeLink role="link" onClick={() => setLinkOpening(true)}>
            {templateName}
          </FakeLink>
        )}
        {matchedRecord &&
          matchedRecord.projectId &&
          matchedRecord.recordId &&
          linkOpening && (
            <GoldenTicketLinkOpener
              matchedRecord={{
                recordId: matchedRecord.recordId,
                projectId: matchedRecord.projectId
              }}
              moduleName={moduleName}
              onClose={() => setLinkOpening(false)}
            />
          )}
      </div>
    </Tooltip>
  )
}

function GoldenTicketLinkOpener({
  matchedRecord,
  moduleName,
  onClose
}: {
  matchedRecord: MatchedRecord
  moduleName: string
  onClose: () => void
}) {
  const accountsAndProjectsQuery = Queries.useUserSelfAccountMappings()

  const account = useMemo(() => {
    if (accountsAndProjectsQuery.isLoading || accountsAndProjectsQuery.error) {
      return null
    }

    return accountsAndProjectsQuery.data.find(am =>
      am.projects.some(p => p.projectId === matchedRecord.projectId)
    )
  }, [
    accountsAndProjectsQuery.data,
    accountsAndProjectsQuery.error,
    accountsAndProjectsQuery.isLoading,
    matchedRecord.projectId
  ])

  const modalContent = useMemo(() => {
    if (accountsAndProjectsQuery.isLoading) {
      return <p>Checking permissions and resolving Account...</p>
    } else if (accountsAndProjectsQuery.error) {
      // Unsure of error type here. Can we get to the Axios error?
      return (
        <p>
          Error loading account information:{' '}
          {accountsAndProjectsQuery.error?.toString()}
        </p>
      )
    } else if (!account) {
      return (
        <p>
          You do not have access to Project{' '}
          <code>{matchedRecord.projectId}</code> which this Golden Record is in.
        </p>
      )
    }
    return <p>Opening Golden Ticket in a new tab...</p>
  }, [
    account,
    accountsAndProjectsQuery.error,
    accountsAndProjectsQuery.isLoading,
    matchedRecord.projectId
  ])

  useEffect(() => {
    if (account) {
      const url = `/accounts/${account.accountId}/projects/${matchedRecord.projectId}/${moduleName}/record-v2/${matchedRecord.recordId}/edit`
      window.open(url, '_blank', 'noreferrer')
      onClose()
    }
  }, [account, matchedRecord, moduleName, onClose])

  return (
    <AntModal
      title="Opening 🎫 Golden Ticket..."
      width={600}
      open={true}
      footer={null}
      onCancel={onClose}
    >
      {modalContent}
    </AntModal>
  )
}
const FakeLink = styled.div`
  color: ${colours.TEAL};
  cursor: pointer;
`
